import { findSceneNode, getAllComponents, type CurvaDocument } from "@/document/model/document";
import { renderComponentThumbnailDataUrl } from "@/export/componentThumbnail";

const thumbnails = new Map<string, string>();
const inFlight = new Set<string>();
let version = 0;
const listeners = new Set<() => void>();

const COMPONENT_PREVIEW_PREFIX = "previews/components/";
const COMPONENT_PREVIEW_SUFFIX = ".png";

function notify(): void {
  version += 1;
  for (const listener of listeners) {
    listener();
  }
}

export function componentThumbnailLabel(name: string): string {
  if (name === "Component") {
    return "Set";
  }
  return name.replace(/ Component$/u, "");
}

export function getComponentThumbnailSnapshot(): number {
  return version;
}

export function getComponentThumbnail(componentId: string): string | undefined {
  return thumbnails.get(componentId);
}

export function subscribeComponentThumbnails(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearComponentThumbnails(): void {
  if (thumbnails.size === 0) {
    return;
  }
  thumbnails.clear();
  notify();
}

export function importComponentThumbnailBytes(entries: Iterable<{ componentId: string; bytes: Uint8Array }>): void {
  let changed = false;
  for (const { componentId, bytes } of entries) {
    if (bytes.length === 0) {
      continue;
    }
    thumbnails.set(componentId, bytesToDataUrl(bytes));
    changed = true;
  }
  if (changed) {
    notify();
  }
}

export function componentThumbnailPackageEntries(document: CurvaDocument): Record<string, Uint8Array> {
  const componentIds = new Set(getAllComponents(document).map(component => component.id));
  const entries: Record<string, Uint8Array> = {};
  for (const [componentId, dataUrl] of thumbnails) {
    if (!componentIds.has(componentId)) {
      continue;
    }
    entries[`${COMPONENT_PREVIEW_PREFIX}${componentId}${COMPONENT_PREVIEW_SUFFIX}`] = dataUrlToBytes(dataUrl);
  }
  return entries;
}

export function importComponentThumbnailsFromPackageEntries(entries: Record<string, Uint8Array | undefined>): void {
  clearComponentThumbnails();
  const loaded: Array<{ componentId: string; bytes: Uint8Array }> = [];
  for (const [path, bytes] of Object.entries(entries)) {
    if (!bytes || !path.startsWith(COMPONENT_PREVIEW_PREFIX) || path === `${COMPONENT_PREVIEW_PREFIX}` || !path.endsWith(COMPONENT_PREVIEW_SUFFIX)) {
      continue;
    }
    const componentId = path.slice(COMPONENT_PREVIEW_PREFIX.length, -COMPONENT_PREVIEW_SUFFIX.length);
    if (componentId.length === 0) {
      continue;
    }
    loaded.push({ componentId, bytes });
  }
  importComponentThumbnailBytes(loaded);
}

export async function refreshComponentThumbnail(document: CurvaDocument, componentId: string): Promise<void> {
  if (inFlight.has(componentId)) {
    return;
  }

  const component = findSceneNode(document, componentId);
  if (!component || component.type !== "component") {
    return;
  }

  inFlight.add(componentId);
  try {
    const dataUrl = await renderComponentThumbnailDataUrl(document, component);
    if (dataUrl) {
      thumbnails.set(componentId, dataUrl);
      notify();
    }
  } finally {
    inFlight.delete(componentId);
  }
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) {
    return new Uint8Array();
  }

  const base64 = dataUrl.slice(commaIndex + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function bytesToDataUrl(bytes: Uint8Array, mimeType = "image/png"): string {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]!);
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
}
